import { getSession, useSession } from 'next-auth/react';
import { appWithTranslation, useTranslation } from 'next-i18next';
import type { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import NextNProgress from 'nextjs-progressbar';
import { useEffect } from 'react';
import { setDateUtilsConfig, updateDateUtilsConfig } from '@ninja/config';
import { initiateLogger } from '@ninja/config/dist/enhanced-logger';
import { useLiveChatWidget } from '@ninja/nextjs';
import { PersistorSuspense, SystemLocale } from '@ninja/utilities';
import { NinjaThemeProvider } from '@ninja/web';
import '@ninja/web/dist/tailwind/main.css';
import '@ninja/web/dist/tailwind/theme/default.css';
import {
	AppInitStoreProvider,
	QuoteStoreProvider,
	createJSONStorage,
	setCustomerLibConfig,
} from '@shory-health-individual/customer-lib';
import { ShorySessionProvider } from '@shory/auth/dist/ShorySessionProvider';
import ErrorBoundary from '../components/ErrorBoundary';
import AppProvider from '../config/AppProvider';
import { AppConfig } from '../config/app-config';
import { routesPermissions } from '../config/routes-and-permissions';
import { ShoryNextNavigationProvider, ShoryPagePermissionGate } from '../contexts/navigation';
import useLayoutSetting from '../hooks/useLayoutSetting';
import '../styles/global.css';

declare global {
	// eslint-disable-next-line no-var
	var njTimeStamp: number;
}

initiateLogger();

setDateUtilsConfig({
	isoFormat: 'utc',
	timezone: 'Asia/Dubai',
});

setCustomerLibConfig({
	isProduction: AppConfig.isProduction,
	healthIndividualApiUrl: AppConfig.healthIndividualApiUrl,
	authApiUrl: AppConfig.authApiUrl,
});

const App = ({ Component, pageProps }: AppProps) => {
	if (pageProps.isLanding) {
		return <Component {...pageProps} />;
	}

	return (
		<PersistorSuspense>
			<Component {...pageProps} />
		</PersistorSuspense>
	);
};

const AppProviders = (props: AppProps) => {
	const { status, data } = useSession();
	const liveChat = useLiveChatWidget({
		appConfig: {
			...AppConfig.omniChannelConfig,
		},
		productName: 'Health Individual',
		userInfo: {
			isUserLoggedIn: !!data,
			user: {
				email: data?.user.email,
				mobileNumber: data?.user.phone,
			},
		},
	});
	const layoutSetting = useLayoutSetting({ liveChat });

	if (status === 'loading') return <></>;

	return (
		<NinjaThemeProvider {...layoutSetting}>
			<AppProvider locale={props.router.locale as SystemLocale} liveChat={liveChat}>
				<ShoryNextNavigationProvider>
					<ShoryPagePermissionGate>
						<App {...props} />
					</ShoryPagePermissionGate>
				</ShoryNextNavigationProvider>
			</AppProvider>
		</NinjaThemeProvider>
	);
};
const AppRoot = (props: AppProps) => {
	const [t, i18n] = useTranslation();
	const isMemberPage = props.router.pathname.includes(routesPermissions.QuoteMembers.path);

	useEffect(() => {
		updateDateUtilsConfig({ locale: (i18n.language as SystemLocale) ?? 'en' });
		const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

		document.querySelector('html')?.setAttribute('dir', dir);
	}, [i18n.language]);

	return (
		<>
			<Head>
				<title>{t('shared.product-name')}</title>
				{/* 
					njTimeStamp is used by in the Ninja Lib generate a unique id for the input form.
					It was introduced to have same id in server and client sides rendering and prevent
					the following warning: Prop 'ld ' did not match. Server: "XXX" Client: "YYY"
				 */}
				<script>{`var njTimeStamp = ${globalThis.njTimeStamp ?? Date.now()}`}</script>
				<meta
					name="viewport"
					id="paymentMeta"
					content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
				/>
				{AppConfig.isProduction && isMemberPage ? (
					<>
						{/* Enable indexing on production only */}
						<meta name="robots" content="index, follow" />

						<meta name="title" content={t('shared.meta.title') ?? ''} />
						<meta name="description" content={t('shared.meta.description') ?? ''} />

						<meta property="og:title" content={t('shared.meta.title') ?? ''} />
						<meta property="og:description" content={t('shared.meta.description') ?? ''} />
					</>
				) : (
					<meta name="robots" content="noindex, nofollow" />
				)}
			</Head>
			<QuoteStoreProvider devtoolsEnabled={!AppConfig.isProduction} storage={createJSONStorage(() => sessionStorage)}>
				<AppInitStoreProvider
					devtoolsEnabled={!AppConfig.isProduction}
					storage={createJSONStorage(() => sessionStorage)}
				>
					<ErrorBoundary>
						<NextNProgress options={{ showSpinner: false }} />
						<ShorySessionProvider session={props.pageProps.session}>
							<AppProviders {...props} />
						</ShorySessionProvider>
					</ErrorBoundary>
				</AppInitStoreProvider>
			</QuoteStoreProvider>
		</>
	);
};

AppRoot.getInitialProps = async ({ Component, ctx }: AppContext) => {
	const session = await getSession(ctx);
	let pageProps = {};

	if (Component.getInitialProps) {
		pageProps = await Component.getInitialProps(ctx);
	}

	return {
		pageProps: {
			...pageProps,
			session: session === null ? undefined : session,
		},
	};
};

export default appWithTranslation(AppRoot);
